import React, { useState } from "react";
import { Loader2, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { WorkflowRunViewStepLoading } from "./WorkflowRunViewStepLoading";
import { CollapsedOperationDisplay } from "./CollapsedOperationDisplay";
import { StepWithOperations } from "./WorkflowRunViewWithOperations";

interface GroupedStepLoadingProps {
  steps: StepWithOperations[];
  hasFailures: boolean;
}

/**
 * Displays a grouped section of step loading indicators.
 * Shows "Agent Running" with a spinner.
 * When expanded, shows all individual WorkflowRunViewStepLoading components
 * along with any operations associated with each step.
 */
export const GroupedStepLoading: React.FC<GroupedStepLoadingProps> = ({
  steps,
  hasFailures,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="pl-11">
      <div className="border rounded-lg overflow-hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs bg-muted hover:bg-muted/80 transition-colors"
        >
          {hasFailures ? (
            <AlertCircle className="h-3 w-3 text-destructive shrink-0" />
          ) : (
            <Loader2 className="h-3 w-3 text-blue-500 shrink-0 animate-spin" />
          )}
          <span className="flex-1 text-left font-medium">
            {hasFailures ? "Agent Error" : "Agent Running"}
          </span>
          {isExpanded ? (
            <ChevronUp className="h-3 w-3 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
        </button>
        <div
          className={`${isExpanded ? "max-h-[800px]" : "max-h-0"} transition-all duration-200 ease-in-out overflow-hidden`}
        >
          <div className="p-3 space-y-2">
            {steps.map(({ step, nodeType, nodeName, operations }) => (
              <div key={step.id} className="space-y-2">
                <WorkflowRunViewStepLoading
                  step={step}
                  nodeType={nodeType}
                  nodeName={nodeName}
                />
                {operations && operations.length > 0 && (
                  <div className="ml-4 space-y-1">
                    {operations.map(({ operation }) => (
                      <CollapsedOperationDisplay
                        key={operation.id}
                        operation={operation}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
